import type {
  BrowsePage,
  ConnectorAvailability,
  ConnectorConnection,
  ConnectorFolder,
  ConnectorItemPage,
  ConnectorRoot,
  ConnectorSurface,
  ConnectorSync,
  Locale,
  PendingChanges,
  PreviewSummary,
  UpdateSelectionRequest
} from '@ralph/contracts'
import { apiGet, apiSend } from '@/api/client.ts'

/**
 * Connected folders (PRD-25): the calls behind the Connected folders tab.
 *
 * Every path is `/api/connectors/*` because that is what the server calls them; the words a
 * person reads live in `i18n/messages/<locale>/knowledgeFolders.ts`.
 *
 * Who may do what is the server's (PRD-26 D-38): every folder call needs the caller to maintain
 * the folder's knowledge base; anything that reads THROUGH an account — starting, changing what
 * is included, checking, resuming, retrying, fetching again, a different account — also needs
 * the account to be theirs, or an administrator; and only administrators add company accounts.
 * The list of accounts answers with exactly the ones the caller may use.
 */

export const fetchAvailability = (): Promise<ConnectorAvailability> => apiGet('/api/connectors/availability')

export const fetchConnections = (): Promise<{ items: ConnectorConnection[] }> => apiGet('/api/connectors/connections')

/** Where to send the browser: a full top-level navigation, never a fetch. */
export const startSignIn = (input: {
  provider: 'microsoft' | 'google'
  sourceId: string
  reconnectConnectionId?: string
}): Promise<{ authorizeUrl: string }> => apiSend('/api/connectors/sign-in', 'POST', input)

export type KeylessConnectionInput =
  | {
      provider: 's3'
      accessKeyId: string
      secretAccessKey: string
      sessionToken?: string
      region: string
      endpoint?: string
      forcePathStyle?: boolean
      bucket: string
    }
  | { provider: 'google'; serviceAccountKey: string }
  | { provider: 'microsoft'; tenantId: string }

export const createConnection = (input: KeylessConnectionInput): Promise<ConnectorConnection> =>
  apiSend('/api/connectors/connections', 'POST', input)

export const removeConnection = (id: string): Promise<{ revoked: boolean; pausedFolders: number }> =>
  apiSend(`/api/connectors/connections/${id}`, 'DELETE')

export const browseConnection = (
  id: string,
  input: { surface: ConnectorSurface; at?: Record<string, string>; next?: string; query?: string }
): Promise<BrowsePage> => apiSend(`/api/connectors/connections/${id}/browse`, 'POST', input)

export const fetchS3Policy = (bucket: string, prefix: string): Promise<{ policy: string }> =>
  apiGet(`/api/connectors/s3-policy?${new URLSearchParams({ bucket, prefix })}`)

/** The folders feeding one knowledge base (its maintainers only, PRD-26 D-38). */
export const fetchFolders = (baseId: string): Promise<{ items: ConnectorFolder[] }> =>
  apiGet(`/api/knowledge-bases/${encodeURIComponent(baseId)}/folders`)

export const fetchFolder = (id: string): Promise<ConnectorFolder> => apiGet(`/api/connectors/folders/${id}`)

export const createFolder = (input: {
  sourceId: string
  connectionId: string
  root: ConnectorRoot
  displayPath: string
}): Promise<ConnectorFolder> => apiSend('/api/connectors/folders', 'POST', input)

export const fetchPreview = (id: string): Promise<PreviewSummary> => apiGet(`/api/connectors/folders/${id}/preview`)

export type ItemFilter = 'all' | 'included' | 'unsupported' | 'too_large' | 'filtered' | 'left_out' | 'failed'

export const fetchItems = (
  id: string,
  input: { parent?: string | null; offset?: number; limit?: number; filter?: ItemFilter; query?: string }
): Promise<ConnectorItemPage> => {
  const params = new URLSearchParams()
  if (input.parent) params.set('parent', input.parent)
  if (input.offset) params.set('offset', String(input.offset))
  if (input.limit) params.set('limit', String(input.limit))
  if (input.filter && input.filter !== 'all') params.set('filter', input.filter)
  if (input.query) params.set('query', input.query)
  const query = params.toString()
  return apiGet(`/api/connectors/folders/${id}/items${query ? `?${query}` : ''}`)
}

export const fetchLeftOut = (id: string): Promise<{ items: ConnectorItemPage['items'] }> =>
  apiGet(`/api/connectors/folders/${id}/left-out`)

export const fetchSyncs = (id: string): Promise<{ items: ConnectorSync[] }> =>
  apiGet(`/api/connectors/folders/${id}/syncs`)

/**
 * Starts watching. `ackText` is the sentence the maintainer read, and `ackLocale` the language
 * they read it in (PRD-27 D-7) — this flow keeps only the language, because the folder row
 * already records the acknowledgement structurally: who, when, which access, which groups.
 */
export const startFolder = (
  id: string,
  ack: { ack?: true; ackText?: string; ackLocale?: Locale }
): Promise<ConnectorFolder> => apiSend(`/api/connectors/folders/${id}/start`, 'POST', ack)

export const previewSelection = (id: string, request: UpdateSelectionRequest): Promise<PendingChanges> =>
  apiSend(`/api/connectors/folders/${id}/selection/preview`, 'POST', request)

export const applySelection = (
  id: string,
  request: UpdateSelectionRequest
): Promise<{ folder: ConnectorFolder; applied: PendingChanges }> =>
  apiSend(`/api/connectors/folders/${id}/selection`, 'POST', request)

export const putBack = (id: string, remoteId: string): Promise<ConnectorFolder> =>
  apiSend(`/api/connectors/folders/${id}/put-back`, 'POST', { remoteId })

export type CheckOutcome = {
  queued: boolean
  outcome?: { kind: string; added: number; changed: number; removed: number; withdrawn: number; reason?: string }
  folder: ConnectorFolder
}

export const checkNow = (id: string, walk = false): Promise<CheckOutcome> =>
  apiSend(`/api/connectors/folders/${id}/sync`, 'POST', { walk })

export const pauseFolder = (id: string): Promise<ConnectorFolder> =>
  apiSend(`/api/connectors/folders/${id}/pause`, 'POST', {})
export const resumeFolder = (id: string): Promise<ConnectorFolder> =>
  apiSend(`/api/connectors/folders/${id}/resume`, 'POST', {})

export const retryFailed = (id: string): Promise<{ queued: number; folder: ConnectorFolder }> =>
  apiSend(`/api/connectors/folders/${id}/retry-failed`, 'POST', {})

export const updateFolder = (
  id: string,
  patch: { intervalMs?: number; connectionId?: string }
): Promise<ConnectorFolder> => apiSend(`/api/connectors/folders/${id}`, 'PATCH', patch)

export const disconnectFolder = (
  id: string,
  withdraw: boolean
): Promise<{ disconnected: boolean; withdrawn: number; kept: number }> =>
  apiSend(`/api/connectors/folders/${id}`, 'DELETE', { withdraw })

export const refetchItem = (itemId: string): Promise<{ queued: boolean }> =>
  apiSend(`/api/connectors/items/${itemId}/fetch`, 'POST', {})
